import type { FunctionComponent } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";
import "swiper/css";
import "swiper/css/pagination";
import { Autoplay, Navigation, Pagination } from "swiper/modules";

import { checkIsPostiesArticle } from "scmp-app/components/article/article-render/posties/helpers";
import type { heroArticlesContentQuery } from "scmp-app/queries/__generated__/heroArticlesContentQuery.graphql";

import {
  Container,
  Hero,
  Sand,
  StyledArrowNext,
  StyledArrowPrevious,
  StyledSwiper,
  StyledSwiperSlide,
  SwiperButton,
  SwiperContainer,
} from "./styles";

type Props = {
  queueName: string;
};

export const HeroArticlesContent: FunctionComponent<Props> = ({ queueName }) => {
  const data = useLazyLoadQuery<heroArticlesContentQuery>(
    graphql`
      query heroArticlesContentQuery($queueName: String) {
        queue: queue(filter: { name: $queueName }) {
          items(first: 10) {
            edges {
              node {
                ... on Article {
                  ...helpersCheckIsPostiesArticle
                  entityId
                  headline
                  urlAlias
                  images {
                    url
                    type
                  }
                }
              }
            }
          }
        }
      }
    `,
    {
      queueName,
    },
    { fetchPolicy: "store-or-network" },
  );

  const nodes = data?.queue?.items?.edges
    .filter(({ node }) => checkIsPostiesArticle(node))
    .slice(0, 5);

  if (!nodes || nodes.length === 0) return null;

  return (
    <Container>
      <SwiperContainer>
        <StyledSwiper
          autoplay={{
            delay: 8000,
            disableOnInteraction: false,
          }}
          loop
          modules={[Autoplay, Pagination, Navigation]}
          navigation={{
            disabledClass: "swiper-button-disabled",
            nextEl: ".hero__image-swiper-button-next",
            prevEl: ".hero__image-swiper-button-prev",
          }}
          pagination={{
            clickable: true,
          }}
        >
          {nodes.map(({ node }) => {
            const [firstImage] = node?.images ?? [];
            const image = node?.images?.find(image => image?.type === "hero") ?? firstImage;

            const heroImageProps = image?.url
              ? {
                  $url: image.url,
                }
              : {};

            return (
              <StyledSwiperSlide key={node?.entityId}>
                <Hero {...heroImageProps} href={node.urlAlias} title={node.headline ?? ""} />
              </StyledSwiperSlide>
            );
          })}
        </StyledSwiper>
        <div className="hero__swiper-button hero__image-swiper-button-prev">
          <SwiperButton>
            <StyledArrowPrevious />
          </SwiperButton>
        </div>
        <div className="hero__swiper-button hero__image-swiper-button-next">
          <SwiperButton $isNext={true}>
            <StyledArrowNext />
          </SwiperButton>
        </div>
      </SwiperContainer>
      <Sand />
    </Container>
  );
};

HeroArticlesContent.displayName = "HeroArticlesContent";
